import { useState, useEffect } from "react";
import API from "api/endpoints";
import { useParams } from "react-router-dom";
import axiosInstance from "api/axiosInstance";
import { message } from "antd";

function usePersonalInformationService() {
  const { jobApplicantId } = useParams();

  const [emailExists, setEmailExists] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    personalId: 0,
    image: "",
    employeeNumber: "",
    workEmail: "",
  });

  const [employeeData, setEmployeeData] = useState({
    employeeId: "",
    email: "",
    departmentId: 0,
    employeeName: "",
  });

  const [formDataError, setFormDataError] = useState({
    imageError: "",
    workEmailError: "",
  });

  useEffect(() => {
    fetchEmployeeById(jobApplicantId);
  }, [jobApplicantId]);

  async function fetchEmployeeById(jobApplicantId) {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axiosInstance.get(API.hiringJobApplicant.getById(jobApplicantId));

      const firstEmployee = response.data;
      if (firstEmployee) {
        // GET departmentId and store it in employeeData
        const departmentId = firstEmployee?.jobPosting?.departmentId || 0;
        const employeeName = `${firstEmployee.firstName} ${firstEmployee.lastName}`;

        setEmployeeData({
          jobApplicantId: firstEmployee.jobApplicantId || 0,
          email: firstEmployee.email || "",
          departmentId: departmentId, // Store departmentId
          employeeName: employeeName,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error in fetching employee data");
    } finally {
      setIsLoading(false);
    }
  }

  const handleImageChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFormData((prevData) => ({
        ...prevData,
        image: selectedFile,
      }));
      setFormDataError((prevState) => ({
        ...prevState,
        imageError: "",
      }));
    } else {
      setFormDataError((prevState) => ({
        ...prevState,
        imageError: "Image is required.",
      }));
    }
  };

  const handleChange = async (e) => {
    if (!e || !e.target) return;

    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validation
    if (value.trim() === "") {
      setFormDataError((prev) => ({
        ...prev,
        [`${name}Error`]: `${name} is required.`,
      }));
    } else {
      setFormDataError((prev) => ({
        ...prev,
        [`${name}Error`]: "",
      }));
    }

    // Only for Work Email
    if (name === "workEmail") {
      setEmailExists(false);
      setFormDataError((prev) => ({
        ...prev,
        workEmailError: "",
      }));
    }
  };

  const handleemployeeDataChange = (e) => {
    if (!e || !e.target) {
      console.error("Event or target is undefined:", e);
      return;
    }
    const { name, value } = e.target;
    // Clear errors if a valid value is selected
    setFormDataError({ ...formDataError, [`${name}Error`]: "" });
    // Update state for other fields
    setEmployeeData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  function validatePersonalValidation(formData, setFormDataError) {
    let hasError = false;

    // Check the required fields
    const requiredFields = ["image", "workEmail"];

    requiredFields.forEach((fieldName) => {
      if (
        !formData[fieldName] ||
        (typeof formData[fieldName] === "string" && !formData[fieldName].trim())
      ) {
        const fieldNameWithSpaces = fieldName
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase());
        setFormDataError((prevState) => ({
          ...prevState,
          [`${fieldName}Error`]: `${fieldNameWithSpaces} is required.`,
        }));
        hasError = true;
      }
    });

    return !hasError;
  }

  // New function to initialize form with data from parent component
  const initializeWithInitialData = (initialData) => {
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        ...initialData,
      };

      return updatedFormData;
    });
  };

  const verifyEmail = async () => {
    if (!formData.workEmail) {
      setFormDataError((prev) => ({
        ...prev,
        workEmailError: "Work email is required",
      }));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.workEmail)) {
      setFormDataError((prev) => ({
        ...prev,
        workEmailError: "Please enter a valid email address",
      }));
      return;
    }

    try {
      const response = await axiosInstance.get(
        API.employee.checkWorkEmailExists(formData.workEmail)
      );

      if (response.data.exists) {
        setEmailExists(true);
        setIsEmailVerified(false);

        setFormDataError((prev) => ({
          ...prev,
          workEmailError: response.data.message || "Work email already exists",
        }));
      } else {
        setEmailExists(false);
        setIsEmailVerified(true);

        setFormDataError((prev) => ({
          ...prev,
          workEmailError: "",
        }));

        message.success("Email is available");
      }
    } catch (error) {
      console.error("Email verification error:", error);
    }
  };

  return {
    formData,
    setFormData,
    formDataError,
    setFormDataError,
    handleChange,
    handleImageChange,
    validatePersonalValidation,
    employeeData,
    handleemployeeDataChange,
    emailExists,
    initializeWithInitialData,
    error,
    isLoading,
    verifyEmail,
    isEmailVerified,
  };
}

export default usePersonalInformationService;
